import Chart from "react-apexcharts";
import { dimensionCurrent } from "../../../../services/api-services";
import { toastEmitter } from "../../../../utills/utilities";
import { useEffect, useState, useMemo, useCallback } from "react";
import { API_RESPONSE } from "../../../../utills/app-constant";
import generateChartImage from "../generateChartImage";
import Remark from "../Remark";
import DownloadGraphButton from "../../../../user/pages/page/DownloadGraphButton";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const DimenstionCurrentChart = ({ selectedCompanyId, graphName, myhandleExport }) => {
  const [responseBody, setResponseBody] = useState([]);
  const [loading, setLoading] = useState(false);
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState(null);
  const [isAttempt, setIsAttempt] = useState("");

  const getDimenstionCurrentData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dimensionCurrent(selectedCompanyId, graphName);
      if (response?.data?.responseCode === 200) {
        setResponseBody(response?.data?.responseBody || []);
        setRemark(response?.data?.remark || "");
        setRemarkId(response?.data?.remarkId || null);
        setIsAttempt(response?.data?.isAttempt || null);
      } else {
        setResponseBody([]);
        toastEmitter("error", response?.data?.message);
      }
    } catch (err) {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, graphName]);

  useEffect(() => {
    if (selectedCompanyId) {
      getDimenstionCurrentData();
    }
  }, [selectedCompanyId, getDimenstionCurrentData]);

  const generateChartData = useMemo(() => {
    return (subdimension) => {
      const questionResponseList = subdimension?.questionResponseList || [];
      if (questionResponseList.length === 0) {
        return { options: {}, series: [] };
      }

      const criteriaLabels = questionResponseList.map((item) => item?.criteriaShortLabel || "No Data");

      const seriesData = [
        { name: "Initiating", data: [] },
        { name: "Emerging", data: [] },
        { name: "Performing", data: [] },
        { name: "Advancing", data: [] },
        { name: "Leading", data: [] },
      ];

      questionResponseList.forEach((item) => {
        seriesData[0].data.push(item?.initiatingValue ?? 0);
        seriesData[1].data.push(item?.emergingValue ?? 0);
        seriesData[2].data.push(item?.performingValue ?? 0);
        seriesData[3].data.push(item?.advancingValue ?? 0);
        seriesData[4].data.push(item?.leadingValue ?? 0);
      });

      const chartID = `customer_dimension_current${subdimension.subDimensionName.replace(/\s+/g, "_")}`;

      return {
        options: {
          chart: {
            id: chartID,
            type: "bar",
            events: {
              mounted: () => {
                setTimeout(() => {
                  generateChartImage(chartID, "customer_dimension_current", responseBody?.remark);
                }, 500);
              },
            },
            height: 350,
            stacked: true,
            stackType: "100%",
          },
          plotOptions: {
            bar: {
              horizontal: true,
            },
          },
          stroke: {
            width: 0.2,
            colors: ["#fff"],
          },
          grid: {
            show: false,
          },
          dataLabels: {
            enabled: true,
            style: {
              colors: ["#000"],
              fontSize: "9px",
            },
          },
          yaxis: {
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          title: { text: "" },
          xaxis: {
            categories: criteriaLabels,
            labels: { show: true },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          tooltip: {
            y: { formatter: (val) => `${val}K` },
          },
          fill: {
            opacity: 1,
            colors: ["#D42327", "#fd8000", "#ffbf01", "#dbc51a", "#92d14e"],
          },
          legend: {
            position: "top",
            offsetX: 100,
            markers: {
              fillColors: ["#D42327", "#fd8000", "#ffbf01", "#dbc51a", "#92d14e"],
            },
          },
        },
        series: seriesData,
      };
    };
  }, [responseBody]);


  const exportToExcel = (data, fileName = "Dimension - Current") => {
    if (!data || !Array.isArray(data)) return;

    const heading = ["Dimension - Current"];
    const headers = [
      "Subdimension Name",
      "Initiating Value",
      "Emerging Value",
      "Performing Value",
      "Advancing Value",
      "Leading Value"
    ];

    const rows = [];

    data.forEach(dimension => {
      dimension?.dimensionResponseList?.forEach(sub => {
        let initiating = 0, emerging = 0, performing = 0, advancing = 0, leading = 0;

        sub?.questionResponseList?.forEach(question => {
          initiating += question.initiatingValue || 0;
          emerging += question.emergingValue || 0;
          performing += question.performingValue || 0;
          advancing += question.advancingValue || 0;
          leading += question.leadingValue || 0;
        });

        rows.push([
          sub.subDimensionName || "",
          initiating.toFixed(2),
          emerging.toFixed(2),
          performing.toFixed(2),
          advancing.toFixed(2),
          leading.toFixed(2)
        ]);
      });
    });

    const worksheet = XLSX.utils.aoa_to_sheet([
      heading,
      [],
      headers,
      ...rows
    ]);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Subdimension Summary");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array"
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });

    saveAs(blob, fileName);
  };

  return (
    <>

      <div className="stackedChart_content">
        {loading ? (
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        ) : responseBody?.length > 0 ? (
          responseBody.map((dimension, dimensionIndex) =>
            dimension?.dimensionResponseList?.map((subdimension, subIndex) => {
              if (!subdimension?.questionResponseList || subdimension?.questionResponseList.length === 0) {
                return (
                  <div key={`${dimensionIndex}-${subIndex}`} className="no-data-container">
                    <p>No data available for {subdimension?.subDimensionName || "Subdimension"}</p>
                  </div>
                );
              }

              const { options, series } = generateChartData(subdimension);

              return (
                <div key={`${dimensionIndex}-${subIndex}`}>
                  <div className="d-flex justify-content-between">
                    <h5>{subdimension?.subDimensionName}</h5>
                    <div className="d-flex">
                      <DownloadGraphButton chartID={`chart18-${subdimension?.id}`} myhandleExport={myhandleExport} />

                      <button className="px-3 py-2 mb-3 text-white bg-primary rounded border-0 ms-2" onClick={() => exportToExcel(responseBody)}>Download Excel</button>

                    </div>

                  </div>
                  <div className={`chart-container pdf-chart chart18-${subdimension?.id}`} data-heading={subdimension?.subDimensionName} data-remark={remark}>
                    <Chart options={options} series={series} type="bar" height={250} />
                  </div>
                </div>
              );
            })
          )
        ) : (
          <p>No data available</p>
        )}
        <Remark
          selectedCompanyId={selectedCompanyId}
          graphName="CustomerDimensionCurrent"
          initialRemark={remark}
          remarkId={remarkId}
          isAttempt={isAttempt}
          setRemarkprops={setRemark}
        />
      </div>


    </>

  );
};

export default DimenstionCurrentChart;

